import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, Brain, Sun, Moon } from 'lucide-react';
import { useTheme } from 'next-themes';

const LandingHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  
  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const navItems = [
    { name: 'Home', href: '/' },
    { name: 'Features', href: '/features' },
    { name: 'Integrations', href: '/integrations' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'About', href: '/about' }
  ];

  return (
    <>
      <style>
        {`
          @media (max-width: 768px) {
            /* CRITICAL FIX: Force header to be clickable */
            header {
              position: relative !important;
              z-index: 9999 !important;
              pointer-events: auto !important;
            }

            header * {
              pointer-events: auto !important;
            }

            /* Force all header elements to be above everything */
            header > div {
              position: relative !important;
              z-index: 10000 !important;
            }

            /* Ensure all buttons are clickable */
            header button {
              position: relative !important;
              z-index: 10001 !important;
              pointer-events: auto !important;
              min-width: 48px !important;
              min-height: 48px !important;
            }
          }
        `}
      </style>
      <header className="fixed top-0 left-0 right-0 z-50 bg-transparent backdrop-blur-lg border-b border-white/10 dark:border-white/5">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src="/synkro.png" alt="Synkro" className="h-7 w-7" />
            <span className="text-lg font-bold text-black dark:text-white drop-shadow-lg dark:drop-shadow-none">Synkro</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8 ml-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-black/80 dark:text-white/80 hover:text-black dark:hover:text-white drop-shadow-sm dark:drop-shadow-none transition-colors font-medium"
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full text-black/80 dark:text-white/80 hover:text-black dark:hover:text-white hover:bg-black/10 dark:hover:bg-white/10"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>
            <Link to="/auth">
              <Button
                variant="ghost"
                className="relative bg-black/10 dark:bg-white/10 backdrop-blur-md hover:bg-black/20 dark:hover:bg-white/20 border border-black/30 dark:border-white/30 text-black dark:text-white hover:text-black dark:hover:text-white drop-shadow-lg dark:drop-shadow-none transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-black/20 dark:hover:shadow-white/20 overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
                <span className="relative z-10 font-medium">Sign In</span>
              </Button>
            </Link>
          </div>

          {/* Mobile Actions */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Mobile Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full text-black/80 dark:text-white/80 hover:text-black dark:hover:text-white hover:bg-black/10 dark:hover:bg-white/10 min-w-[48px] min-h-[48px]"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>
            {/* Mobile Menu Button */}
            <button
              className="p-2 rounded-lg text-black/80 dark:text-white/80 hover:text-black dark:hover:text-white hover:bg-black/10 dark:hover:bg-white/10 min-w-[48px] min-h-[48px] flex items-center justify-center"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-black/20 backdrop-blur-lg border-t border-white/10">
          <div className="px-4 py-6 space-y-4">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="block text-black/80 dark:text-white/80 hover:text-black dark:hover:text-white transition-colors font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <div className="pt-4 border-t border-white/20 space-y-2">
              <Link to="/auth" className="block">
                <Button
                  variant="ghost"
                  className="w-full relative bg-black/10 dark:bg-white/10 backdrop-blur-md hover:bg-black/20 dark:hover:bg-white/20 border border-black/30 dark:border-white/30 text-black dark:text-white hover:text-black dark:hover:text-white transition-all duration-300 hover:scale-[1.02] overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
                  <span className="relative z-10 font-medium">Sign In</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
      </header>
    </>
  );
};

export default LandingHeader; 